import { Controller } from "@hotwired/stimulus"
import * as L from "leaflet"
import { arcgisToGeoJSON } from "@esri/arcgis-to-geojson-utils"

// Import des images depuis le paquet leaflet
import iconUrl from "leaflet/dist/images/marker-icon.png"
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png"
import shadowUrl from "leaflet/dist/images/marker-shadow.png"

L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
})

export default class extends Controller {
  static values = {
    dataUrl: String,
    lat: Number,
    lng: Number,
    zoom: Number
  }

  connect() {
    // Set defaults if not provided
    if (!this.hasDataUrlValue) this.dataUrlValue = "/quartier.json"
    if (!this.hasLatValue) this.latValue = 50.52
    if (!this.hasLngValue) this.lngValue = 3.60
    if (!this.hasZoomValue) this.zoomValue = 12

    this.initMap()
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  async initMap() {
    this.map = L.map(this.element).setView([this.latValue, this.lngValue], this.zoomValue)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map)

    await this.loadGeoData()
  }

  async loadGeoData() {
    try {
      const resp = await fetch(this.dataUrlValue)
      const esriFeatureSet = await resp.json()

      const fc = {
        type: "FeatureCollection",
        features: esriFeatureSet.features.map((f) => ({
          type: "Feature",
          geometry: arcgisToGeoJSON(f.geometry),
          properties: f.attributes,
        })),
      }

      const layer = L.geoJSON(fc, {
        style: (feature) => this.getFeatureStyle(feature),
        onEachFeature: (feature, l) => this.bindPopup(feature, l),
      }).addTo(this.map)

      try {
        this.map.fitBounds(layer.getBounds(), { padding: [20, 20] })
      } catch {
        // Bounds may be empty
      }
    } catch (error) {
      console.error("Error loading geo data:", error)
    }
  }

  getFeatureStyle(feature) {
    const p = feature.properties || {}
    const type = (feature.geometry?.type || "").toLowerCase()

    if (type === "polygon" || p.type === "zone") {
      return { color: "#1976d2", weight: 2, fillColor: "#90caf9", fillOpacity: 0.35 }
    }
    if (type === "linestring" || p.type === "line") {
      return { color: "#e53935", weight: 3 }
    }
    return { color: "#43a047", weight: 2, fillColor: "#a5d6a7", fillOpacity: 0.3 }
  }

  bindPopup(feature, layer) {
    const p = feature.properties || {}
    const label = p.name_fr ?? p.name_nl ?? p.name_de ?? p.id ?? "Géométrie"
    layer.bindPopup(`<strong>${label}</strong>`)
  }
}
